/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 15/10/2025 - 14:47:11
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/10/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState } from 'react';
import { Modal, Button, Nav, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface VideoItem {
  id: string;
  title: string;
  description: string;
  url: string;
}

interface DemoVideoModalProps {
  show: boolean;
  onHide: () => void;
  onDontShowAgain: (videoId: string) => void;
  videos: VideoItem[];
  initialVideoId?: string;
}

const DemoVideoModal: React.FC<DemoVideoModalProps> = ({ 
  show, 
  onHide, 
  onDontShowAgain, 
  videos,
  initialVideoId
}) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const [activeVideoId, setActiveVideoId] = useState(initialVideoId || (videos.length > 0 ? videos[0].id : ''));
  const [videoError, setVideoError] = useState<string | null>(null);

  // Find the currently active video
  const activeVideo = videos.find(video => video.id === activeVideoId) || videos[0];

  const handleVideoError = () => {
    setVideoError("There was an error loading the video. Please try again later or access it from the profile settings.");
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t('dashboard:demoVideo.title', 'Welcome to Solar Stripe')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {videos.length > 1 && (
          <Nav variant="tabs" className="mb-3">
            {videos.map(video => (
              <Nav.Item key={video.id}>
                <Nav.Link 
                  active={activeVideoId === video.id}
                  onClick={() => setActiveVideoId(video.id)}
                >
                  {video.title}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        )}

        {videoError && (
          <Alert variant="warning" onClose={() => setVideoError(null)} dismissible>
            {videoError}
          </Alert>
        )}

        <div className="ratio ratio-16x9 mb-3">
          <video
            key={activeVideo.url} // Key helps React re-render video when source changes
            src={activeVideo.url}
            title={activeVideo.title}
            controls
            autoPlay
            className="w-100 h-100 object-fit-contain"
            onError={handleVideoError}
          />
        </div>
        
        <div className="mt-3">
          <h5>{activeVideo.title}</h5>
          <p>{activeVideo.description}</p>
          
          <Alert variant="info">
            <strong>Note:</strong> You can always access these tutorial videos later through the profile settings.
          </Alert>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button 
          variant="outline-secondary" 
          onClick={() => onDontShowAgain(activeVideoId)}
        >
          {t('dashboard:demoVideo.dontShowAgain', "Don't show this video again")}
        </Button>
        <Button 
          variant="primary" 
          onClick={onHide}
        >
          {t('dashboard:demoVideo.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DemoVideoModal;